import { ParsedCsvRow, AnalysisParams } from '@/components/user/csv-analysis/types';

export interface WinProtectOptimizationResult {
  optimalStartWins: number;
  optimalMaxWins: number;
  totalProfit: number;
  maxDrawdown: number;
  profitDDRatio: number;
  winRate: number;
  totalTrades: number;
  skippedTrades: number;
  effectiveness: number; // Percentage of trades that would be protected
  protectedProfit: number; // Amount of profit protected by the rule
}

export interface WinProtectTestResult extends WinProtectOptimizationResult {
  startWins: number;
  maxWins: number;
  status: 'optimal' | 'good' | 'poor';
  mode?: string;
}

export class WinProtectOptimizer {
  /**
   * Find optimal win protect settings with anti look-ahead bias
   */
  static findOptimalWinProtect(
    csvData: ParsedCsvRow[],
    maxWinsToTest: number,
    params: AnalysisParams
  ): WinProtectOptimizationResult {
    const results = this.findTopWinProtectSettings(csvData, maxWinsToTest, params);
    return results.length > 0 ? results[0] : {
      optimalStartWins: 2,
      optimalMaxWins: 1,
      totalProfit: 0,
      maxDrawdown: 0,
      profitDDRatio: 0,
      winRate: 0,
      totalTrades: 0,
      skippedTrades: 0,
      effectiveness: 0,
      protectedProfit: 0
    };
  }

  /**
   * Find top 10 win protect settings with status classification
   */
  static findTopWinProtectSettings(
    csvData: ParsedCsvRow[],
    maxWinsToTest: number,
    params: AnalysisParams,
    mode?: string
  ): WinProtectTestResult[] {
    // Sort data chronologically to ensure forward-looking analysis
    const sortedData = [...csvData].sort((a, b) => {
      const dateA = new Date(a.timestamp).getTime();
      const dateB = new Date(b.timestamp).getTime();
      return dateA - dateB;
    });

    const results: WinProtectTestResult[] = [];

    // Test combinations of start_wins and max_wins
    for (let startWins = 2; startWins <= Math.min(10, maxWinsToTest); startWins++) {
      for (let maxWins = 1; maxWins <= maxWinsToTest; maxWins++) {
        const result = this.testWinProtectSetting(sortedData, startWins, maxWins, params, mode);
        
        // Classify status based on performance
        let status: 'optimal' | 'good' | 'poor' = 'poor';
        if (result.profitDDRatio >= 3.0 && result.effectiveness >= 30) {
          status = 'optimal';
        } else if (result.profitDDRatio >= 2.0 || result.effectiveness >= 20) {
          status = 'good';
        }
        
        results.push({ 
          ...result, 
          startWins, 
          maxWins,
          status,
          mode: mode || params.win_protect_mode || 'consecutive_trades'
        });
      }
    }

    // Sort by profit/DD ratio (desc), then protected profit (desc), then effectiveness (desc)
    return results.sort((a, b) => {
      if (b.profitDDRatio !== a.profitDDRatio) {
        return b.profitDDRatio - a.profitDDRatio;
      }
      if (b.protectedProfit !== a.protectedProfit) {
        return b.protectedProfit - a.protectedProfit;
      }
      return b.effectiveness - a.effectiveness;
    }).slice(0, 10);
  }

  /**
   * Test a specific win protect setting using time-series validation
   */
  private static testWinProtectSetting(
    csvData: ParsedCsvRow[],
    startWins: number,
    maxWins: number,
    params: AnalysisParams,
    mode?: string
  ): WinProtectOptimizationResult {
    // Apply win protect rule with specified mode
    const testParams = mode ? { ...params, win_protect_mode: mode as 'consecutive_trades' | 'consecutive_losses' } : params;
    const processedData = this.applyWinProtectRule(csvData, startWins, maxWins, testParams);

    // Calculate statistics using normalized PnL
    const activeTrades = processedData.filter(trade => !trade.isSkipped);
    const skippedTrades = processedData.filter(trade => trade.isSkipped);
    
    // Normalize P&L based on TP/SL and quantity
    const normalizedTrades = activeTrades.map(trade => {
      let normalizedPnl = trade.pnl;
      
      if (params.tp_usd > 0 || params.sl_usd > 0) {
        const pq = params.quantity_base > 0 ? (trade.position_size / params.quantity_base) : 1;
        
        if (trade.pnl > 0 && params.tp_usd > 0) {
          normalizedPnl = params.tp_usd * pq;
        } else if (trade.pnl < 0 && params.sl_usd > 0) {
          normalizedPnl = -params.sl_usd * pq;
        }
      }
      
      return { ...trade, normalized_pnl: normalizedPnl };
    });
    
    const winningTrades = normalizedTrades.filter(trade => trade.normalized_pnl > 0);
    const totalProfit = normalizedTrades.reduce((sum, trade) => sum + trade.normalized_pnl, 0);
    const winRate = normalizedTrades.length > 0 ? (winningTrades.length / normalizedTrades.length) * 100 : 0;

    // Calculate protected profit (profit from skipped winning trades in PAUSED state)
    const protectedWinningTrades = skippedTrades.filter(trade => 
      trade.pnl > 0 && trade.skipReason?.includes('PAUSED')
    );
    const protectedProfit = protectedWinningTrades.reduce((sum, trade) => {
      let normalizedPnl = trade.pnl;
      if (params.tp_usd > 0) {
        const pq = params.quantity_base > 0 ? (trade.position_size / params.quantity_base) : 1;
        normalizedPnl = params.tp_usd * pq;
      }
      return sum + normalizedPnl;
    }, 0);

    // Calculate max drawdown
    let maxDrawdown = 0;
    let peak = params.initial_capital || 0;
    let runningPnl = 0;
    
    for (const trade of normalizedTrades) {
      runningPnl += trade.normalized_pnl;
      const currentEquity = (params.initial_capital || 0) + runningPnl;
      peak = Math.max(peak, currentEquity);
      const drawdown = peak - currentEquity;
      maxDrawdown = Math.max(maxDrawdown, drawdown);
    }

    const profitDDRatio = maxDrawdown > 0 ? totalProfit / maxDrawdown : totalProfit > 0 ? Infinity : 0;
    const effectiveness = csvData.length > 0 ? (skippedTrades.length / csvData.length) * 100 : 0;

    return {
      optimalStartWins: startWins,
      optimalMaxWins: maxWins,
      totalProfit,
      maxDrawdown,
      profitDDRatio,
      winRate,
      totalTrades: normalizedTrades.length,
      skippedTrades: skippedTrades.length,
      effectiveness,
      protectedProfit,
    };
  }

  /**
   * Apply win protect rule with forward-looking validation and mode support
   */
  private static applyWinProtectRule(
    trades: ParsedCsvRow[],
    startWins: number,
    maxWins: number,
    params: AnalysisParams
  ): ParsedCsvRow[] {
    const result: ParsedCsvRow[] = [];
    let consecutiveWins = 0;
    let winsInStreak = 0;
    let consecutiveLosses = 0; // For consecutive_losses mode
    let tradesInStreak = 0; // For consecutive_trades mode
    let state: 'WAITING' | 'OPEN' | 'PAUSED' = 'WAITING';
    const mode = params.win_protect_mode || 'consecutive_trades';
    
    for (const trade of trades) {
      const isWin = trade.pnl > 0;
      const isLoss = trade.pnl < 0;
      
      if (state === 'WAITING') {
        if (isWin) {
          consecutiveWins++;
          if (consecutiveWins === startWins) {
            // Skip the Nth win to avoid look-ahead bias, then transition to OPEN
            state = 'OPEN';
            winsInStreak = 0;
            result.push({ 
              ...trade, 
              isSkipped: true, 
              skipReason: `WAITING - Skipping ${startWins}th win to avoid look-ahead bias`
            });
          } else {
            result.push({ 
              ...trade, 
              isSkipped: true, 
              skipReason: `WAITING - Need ${startWins} wins (current: ${consecutiveWins})`
            });
          }
        } else if (isLoss) {
          consecutiveWins = 0;
          result.push({ 
            ...trade, 
            isSkipped: true, 
            skipReason: `WAITING - Loss resets win streak`
          });
        } else {
          // Breakeven doesn't affect streak
          result.push({ 
            ...trade, 
            isSkipped: true, 
            skipReason: `WAITING - Breakeven trade`
          });
        }
      } else if (state === 'OPEN') {
        tradesInStreak++;
        
        if (mode === 'consecutive_trades') {
          // Original mode: stop after M trades (any outcome)
          if (tradesInStreak <= maxWins) {
            if (tradesInStreak === maxWins) {
              // Last allowed trade - decide based on include_nth_win setting
              if (params.win_protect_include_nth_win) {
                result.push({ ...trade, isSkipped: false });
              } else {
                result.push({ 
                  ...trade, 
                  isSkipped: true, 
                  skipReason: `OPEN - ${maxWins}th trade excluded from results`
                });
              }
              state = 'PAUSED';
            } else {
              result.push({ ...trade, isSkipped: false });
            }
          } else {
            // Shouldn't reach here, but safety check
            state = 'PAUSED';
            result.push({ 
              ...trade, 
              isSkipped: true, 
              skipReason: `PAUSED - Exceeded max trades`
            });
          }
        } else {
          // New mode: stop after M consecutive losses
          if (isWin) {
            consecutiveLosses = 0; // Reset loss counter
            result.push({ ...trade, isSkipped: false });
          } else if (isLoss) {
            consecutiveLosses++;
            result.push({ ...trade, isSkipped: false });
            if (consecutiveLosses >= maxWins) {
              // Reached max consecutive losses - transition to PAUSED
              state = 'PAUSED';
            }
          } else {
            // Breakeven trade - doesn't affect loss counter
            result.push({ ...trade, isSkipped: false });
          }
        }
        
        // Reset to WAITING on any loss (for both modes)
        if (isLoss && mode === 'consecutive_trades') {
          consecutiveWins = 0;
          winsInStreak = 0;
          tradesInStreak = 0;
          state = 'WAITING';
        }
      } else if (state === 'PAUSED') {
        if (isLoss) {
          // Loss resets to WAITING
          result.push({ 
            ...trade, 
            isSkipped: true, 
            skipReason: `PAUSED - Loss occurred, resetting to WAITING`
          });
          consecutiveWins = 0;
          winsInStreak = 0;
          consecutiveLosses = 0;
          tradesInStreak = 0;
          state = 'WAITING';
        } else {
          // Skip all trades (wins and breakevens) in PAUSED state
          result.push({ 
            ...trade, 
            isSkipped: true, 
            skipReason: `PAUSED - Waiting for loss to reset`
          });
        }
      }
    }
    
    return result;
  }

  /**
   * Compare all modes and return combined results
   */
  static compareAllModes(
    csvData: ParsedCsvRow[],
    maxWinsToTest: number,
    params: AnalysisParams
  ): WinProtectTestResult[] {
    const modes = ['consecutive_trades', 'consecutive_losses'];
    const allResults: WinProtectTestResult[] = [];
    
    for (const mode of modes) {
      const results = this.findTopWinProtectSettings(csvData, maxWinsToTest, params, mode);
      allResults.push(...results);
    }
    
    // Sort combined results by performance
    return allResults
      .sort((a, b) => {
        if (b.profitDDRatio !== a.profitDDRatio) {
          return b.profitDDRatio - a.profitDDRatio;
        }
        if (b.protectedProfit !== a.protectedProfit) {
          return b.protectedProfit - a.protectedProfit;
        }
        return b.effectiveness - a.effectiveness;
      })
      .slice(0, 15); // Show more results for comparison
  }
}